import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Pencil, SearchX, ShoppingCart } from 'lucide-react';
import { BUSINESS_STATUSES, type BusinessStatus, type SortDirection } from '@quds/shared';
import { CountryDisplay, CountrySelect } from '@/components/location';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Button,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  Pagination,
  SearchInput,
  SkeletonRows,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { listBusinesses, useQuery } from '@/data';
import { ProductionLinkBadge } from '@/features/production-linking/ProductionBadges';
import { BUSINESS_STATUS_LABELS } from '@/features/pharmacies/components/labels';
import { BusinessStatusBadge } from '@/features/pharmacies/components/StatusBadges';
import { cn } from '@/lib/cn';

const PAGE_SIZES = [10, 25, 50, 100] as const;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Supermarket business grid — aligned with Pharmacies grid language. */
export function SupermarketsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const country = params.get('country') ?? '';
  const statusParam = params.get('status') ?? '';
  const status = (BUSINESS_STATUSES as readonly string[]).includes(statusParam) ? (statusParam as BusinessStatus) : '';
  const sortBy = (params.get('sort') as 'name' | 'country' | 'status' | 'createdAt' | 'code') || 'name';
  const sortDir: SortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const sizeParam = Number(params.get('size'));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(sizeParam) ? sizeParam : 25;
  const [searchInput, setSearchInput] = useState(q);
  const debouncedQ = useDebounced(searchInput, 250);

  const patch = (next: Record<string, string | null>, resetPage = true) => {
    setParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (!v) sp.delete(k);
          else sp.set(k, v);
        }
        if (resetPage) sp.set('page', '1');
        return sp;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (debouncedQ !== q) patch({ q: debouncedQ || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    if (q !== debouncedQ && q !== searchInput) setSearchInput(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const filtersActive = !!(q.trim() || country || status);

  const { data, error, loading, reload } = useQuery(
    () =>
      listBusinesses({
        category: 'SUPERMARKET',
        search: debouncedQ || undefined,
        country: country || undefined,
        status: status || undefined,
        sort: sortBy,
        order: sortDir,
        page,
        pageSize,
      }),
    [debouncedQ, country, status, sortBy, sortDir, page, pageSize],
  );

  return (
    <>
      <PageHeader title="Supermarkets" subtitle="Retail and grocery store customers" />
      <div className="card">
        <FilterBar
          dirty={filtersActive}
          onReset={() => {
            setSearchInput('');
            patch({ q: null, country: null, status: null });
          }}
        >
          <SearchInput
            label="Search supermarkets"
            placeholder="Search name or code…"
            value={searchInput}
            onChange={setSearchInput}
            className="w-full lg:max-w-xs"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:items-center">
            <div className="lg:w-56">
              <CountrySelect value={country} onChange={(v) => patch({ country: v || null })} allowEmpty emptyLabel="All countries" />
            </div>
            <FilterSelect
              label="Status"
              allLabel="All"
              value={status}
              onChange={(v) => patch({ status: v || null })}
              options={BUSINESS_STATUSES.map((s) => ({ value: s, label: BUSINESS_STATUS_LABELS[s] }))}
              className="lg:w-48"
            />
          </div>
        </FilterBar>
        {error ? (
          <ErrorState message={error.message} action={<Button variant="secondary" onClick={reload}>Try again</Button>} />
        ) : (
          <TableContainer label="Supermarkets">
            <Table className="min-w-[820px]" aria-busy={loading}>
              <THead>
                <tr>
                  <TH className="pl-5">Name</TH>
                  <TH>Location</TH>
                  <TH>Status</TH>
                  <TH>Production</TH>
                  <TH align="right" className="pr-5">
                    Actions
                  </TH>
                </tr>
              </THead>
              <TBody>
                {loading ? (
                  <SkeletonRows rows={8} columns={5} />
                ) : data && data.items.length > 0 ? (
                  data.items.map((row) => (
                    <TR
                      key={row.id}
                      interactive
                      className="cursor-pointer"
                      onClick={() => navigate(`/supermarkets/${row.id}`)}
                    >
                      <TD className="pl-5">
                        <div className="flex items-center gap-2">
                          <ShoppingCart size={16} className="text-ink-muted" aria-hidden="true" />
                          <div>
                            <Link
                              to={`/supermarkets/${row.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-medium text-ink hover:text-primary hover:underline"
                            >
                              {row.name}
                            </Link>
                            <p className="font-mono text-xs text-ink-muted">{row.code}</p>
                          </div>
                        </div>
                      </TD>
                      <TD>
                        <CountryDisplay country={row.country} city={row.city} />
                      </TD>
                      <TD>
                        <BusinessStatusBadge status={row.status} />
                      </TD>
                      <TD>
                        <ProductionLinkBadge status={row.production.productionLinkStatus} />
                      </TD>
                      <TD align="right" className="pr-5" onClick={(e) => e.stopPropagation()}>
                        <div className={cn('flex items-center justify-end gap-2')}>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/supermarkets/${row.id}`)}>
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Open ${row.name}`}
                            onClick={() => navigate(`/supermarkets/${row.id}?tab=overview`)}
                          >
                            <Pencil size={14} aria-hidden="true" />
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState icon={<SearchX size={20} />} title="No supermarkets" description="Linked production businesses appear here after confirmation." />
                    </td>
                  </tr>
                )}
              </TBody>
            </Table>
            {data && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPageChange={(p) => patch({ page: String(p) }, false)}
                onPageSizeChange={(s) => patch({ size: String(s), page: '1' }, false)}
              />
            )}
          </TableContainer>
        )}
      </div>
    </>
  );
}
